import { EditorErrorBoundary, suppressErrors } from "@prismicio/editor-fields"
import { FatalError } from "@prismicio/editor-support/Error"
import { isAuthError, isRequestError } from "@prismicio/editor-support/Http"
import { cache } from "@prismicio/editor-support/Suspense"
import { DefaultErrorMessage, ErrorBoundary } from "@prismicio/editor-ui"
import { captureException } from "@sentry/react"
import { type PropsWithChildren, useRef, useState } from "react"
import { isRouteErrorResponse, useRouteError } from "react-router-dom"

import { LoginForm } from "../auth/Login"
import { env } from "../env"
import { clearExperiments } from "../integrations/AmplitudeExperiments"
import { shutdownIntercom } from "../integrations/intercom"
import { getRouterBasename, isParamsValidationError } from "../routing"
import {
  getBaseRepositoryUrl,
  isRepositoryLockedError,
} from "../routing/repository"
import { router } from "../routing/routes"
import { trackGlobalErrorScreenDisplayed } from "../services/tracking"
import { queryClient } from "./providers/QueryProvider"

export function AppErrorHandler({ children }: PropsWithChildren) {
  const resetErrorBoundaryRef = useRef(() => {})
  const [showLogin, setShowLogin] = useState(false)

  function interceptError(error: unknown, reset: () => void) {
    resetErrorBoundaryRef.current = reset

    // Check if the error is a repository locked error, this need to happen before the auth error is handled
    // because the auth error might be a repository locked error as both are 403 errors
    if (isRepositoryLockedError(error)) {
      // Redirect to subscriptions plan settings page when repository is locked
      window.location.href = `${getBaseRepositoryUrl()}/settings/plan`
      return true
    }

    // Auth errors are a normal flow of the app and are handled centrally, here.
    if (isAuthError(error)) {
      shutdownIntercom()
      clearExperiments()

      if (env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY === "token") {
        // Wait for the Error Boundary to finish rendering before we show the login screen.
        setTimeout(() => setShowLogin(true))
      } else {
        // Delegate to the Wroom login screen till Page Builder can handle a cookie based authentication itself.
        window.location.href = window.location.origin
      }

      return true
    }

    // Routing error, either route mismatch or bad param validation.
    // There's no way retrying at the local boundary level is going to help so we handle it here and redirect to the safest place we can.
    if (isRouteErrorResponse(error) || isParamsValidationError(error)) {
      captureException(error)

      if (import.meta.env.DEV) console.error(error)

      window.location.href = getRouterBasename()
      return true
    }

    // In order to spare our Sentry quotas, we assume any request error would already be covered by the services' own reporting.
    if (!isRequestError(error)) {
      captureException(error)
    }

    // We don't know how to handle this error: let the local boundary handle it.
    return false
  }

  async function onLoggedIn() {
    // Start from a blank slate; also because the provided credentials
    // might be different and returned data may vary.
    cache.clearAll()
    queryClient.clear()

    // Revalidate the router loaders and clear any current Router error.
    await router.navigate(".", { replace: true })

    // Start re-rendering the happy path components again.
    resetErrorBoundaryRef.current()

    setShowLogin(false)
  }

  // No side effects. This needs to be done before we render our children
  // as they could throw during their first render.
  ErrorBoundary.registerGlobalErrorInterceptor(interceptError)

  return (
    <>
      <EditorErrorBoundary
        renderError={renderError}
        onError={trackGlobalErrorScreenDisplayed}
      >
        {children}
      </EditorErrorBoundary>
      <LoginForm shown={showLogin} onLoggedIn={onLoggedIn} />
    </>
  )
}

function renderError(error: unknown) {
  // For now we don't have a great strategy to handle Fatal errors.
  // Ideally we redirect to a 100% safe location like a static page so we can avoid infinite redirect loops.
  // With FatalError, there's nothing to retry that could succeed the second time if it didn't the first time
  // so we don't get customers' hopes up by showing the retry button.
  const canRetry = !(error instanceof FatalError)

  return (
    <DefaultErrorMessage
      title="Prismic has encountered an error"
      sx={{ marginTop: 120 }}
      canRetry={canRetry}
    />
  )
}

export function RouterError() {
  const error = useRouteError()

  // Trick the JSX types. The error is always going to be defined.
  if (error) throw error

  return <></>
}

suppressErrors()
